
import { SourceFile, useAnnotations, useWalkthroughParser, WalkthroughHandler, AnnotationHandler, CodeEditorInstance, Annotation, DoublyLinkedNode, SelectionRange, FocusMode } from "@sandbox/code-editor";
import { useEffect, useState } from "react";
import { useMarkdownOptions } from "./useMarkdownOptions";
import { useAppDispatch } from "store";
import { openFile } from "store/features";
import { WalkthroughInitializer } from "./WalkthroughInitializer";

export interface WalkthroughProps {
  editor: CodeEditorInstance;
  sandboxName?: string;
  files?: SourceFile[];
  className?: string;
  readOnly?: boolean
}

export const Walkthrough = ({ editor, sandboxName, files, className, readOnly }: WalkthroughProps) => {
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState<DoublyLinkedNode<Annotation>>();

  const annotationHandler = editor.instance ? new AnnotationHandler(editor.instance) : undefined;
  const walkthroughSteps = useWalkthroughParser({ sandboxName, files });
  useAnnotations({ walkthroughSteps, annotationHandler });

  const markdownOptions = useMarkdownOptions({
    onStepChanged: (annotationId: string) => {
      if (walkthroughSteps) {
        const step = walkthroughSteps.find((step) => step.value.id === annotationId);
        if (step)
          setActiveStep(step);
      }
    }
  });

  const revealFile = (focusMode: FocusMode) => {
    if (!activeStep?.value.filePath)
      return;

    let selection: SelectionRange | undefined;
    if (activeStep.value.startLineNumber !== undefined && activeStep.value.endLineNumber !== undefined)
      selection = {
        startLineNumber: activeStep.value.startLineNumber,
        endLineNumber: activeStep.value.endLineNumber,
        startColumn: 0,
        endColumn: Infinity
      };

    dispatch(openFile({ filePath: activeStep.value.filePath, selection, focusMode }));
  }

  const onFocusModeChanged = (enabled: boolean) => {
    revealFile(enabled ? "dimmed" : "none");
  };

  const onRecentered = () => {
    if (activeStep)
      revealFile("dimmed");
  }

  useEffect(() => {
    if (activeStep)
      revealFile("dimmed");
  }, [activeStep]);

  useEffect(() => {
    if (walkthroughSteps)
      setActiveStep(walkthroughSteps.at(0));
  }, [walkthroughSteps])

  if (!annotationHandler)
    return null;

  return (
    <>
      {walkthroughSteps && walkthroughSteps.length
        ? <WalkthroughHandler
          className={className}
          annotationHandler={annotationHandler}
          activeStep={activeStep}
          onStepChanged={setActiveStep}
          annotationSteps={walkthroughSteps}
          markdownOptions={markdownOptions}
          onFocusModeChanged={onFocusModeChanged}
          onRecentered={onRecentered}
        />
        : !readOnly && <WalkthroughInitializer className={className} />
      }
    </>
  );
}